import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { PageLoader } from '@/components/common/PageLoader';
import { cn } from '@/utils/cn';
import { getBreadcrumbs } from '@/config/navigation';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { CommandPalette } from './CommandPalette';

const COLLAPSED_KEY = 'dfupr-sidebar-collapsed';

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

export const MainLayout: React.FC = () => {
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [commandOpen, setCommandOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // без сохранения — состояние живёт до перезагрузки
      }
      return next;
    });
  }, []);

  // Горячие клавиши: ⌘K / Ctrl+K — палитра команд, ⌘B / Ctrl+B — свернуть меню
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
      const key = event.key.toLowerCase();
      if (key === 'k' || key === 'л') {
        event.preventDefault();
        setCommandOpen((open) => !open);
      } else if (key === 'b' || key === 'и') {
        event.preventDefault();
        toggleCollapsed();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleCollapsed]);

  // Новая страница открывается с начала
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);

  // Название вкладки браузера — по текущему разделу: «Приходы · DF-UPR»
  useEffect(() => {
    const crumbs = getBreadcrumbs(location.pathname, location.search);
    const title = crumbs[crumbs.length - 1]?.label;
    document.title = title ? `${title} · DF-UPR` : 'DF-UPR · Управленческий учёт';
  }, [location.pathname, location.search]);

  return (
    <TooltipProvider delayDuration={250} skipDelayDuration={150}>
      <div className="flex h-dvh overflow-hidden bg-sidebar print:block print:h-auto print:overflow-visible print:bg-white">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60] focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:text-primary-foreground"
        >
          Перейти к содержимому
        </a>

        {/* Боковое меню (десктоп) */}
        <aside
          className={cn(
            'hidden shrink-0 transition-[width] duration-200 ease-out lg:block print:!hidden',
            collapsed ? 'w-[60px]' : 'w-[244px]'
          )}
        >
          <Sidebar collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
        </aside>

        {/* Боковое меню (мобильные) */}
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className="w-[280px] max-w-[85vw] gap-0 border-sidebar-border bg-sidebar p-0" hideClose>
            <SheetTitle className="sr-only">Меню</SheetTitle>
            <Sidebar onNavigate={() => setMobileNavOpen(false)} />
          </SheetContent>
        </Sheet>

        {/* Рабочая область */}
        <div className="flex min-w-0 flex-1 flex-col lg:py-2 lg:pr-2 print:block print:p-0">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface lg:rounded-xl lg:border lg:shadow-sm print:block print:overflow-visible print:rounded-none print:border-0 print:shadow-none">
            <Header onOpenMobileNav={() => setMobileNavOpen(true)} onOpenCommand={() => setCommandOpen(true)} />
            <main ref={mainRef} id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto outline-none print:overflow-visible">
              <div className="mx-auto w-full max-w-[1600px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 print:max-w-none print:p-0">
                <ErrorBoundary resetKey={location.pathname}>
                  <Suspense fallback={<PageLoader />}>
                    <Outlet />
                  </Suspense>
                </ErrorBoundary>
              </div>
            </main>
          </div>
        </div>

        <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
      </div>
    </TooltipProvider>
  );
};
